import { UI } from "@wwimmo/ui";
import { ChangeEvent, useCallback, useState } from "react";
import { UseFormSetValue } from "react-hook-form";
import styles from "./ImageSelector.module.css";
import { v4 as uuidv4 } from "uuid";
import Resizer from "react-image-file-resizer";
import { IMAGE_TYPE } from "src/api/Notification";
import { useTranslation } from "react-i18next";

interface ImageSelectorItemProps {
    label: string;
    name: string;
    setValue: UseFormSetValue<any>;
    initialImageUrl?: string;
    imagetype: IMAGE_TYPE;
}
interface FileHTMLInputEvent extends ChangeEvent {
    target: HTMLInputElement & EventTarget;
}
const dataTypes = ["image/jpeg", "image/png"];
const extensions = [".jpg", ".jpeg", ".png"];

const MAX_IMAGE_DIMENSION = {
    WIDTH: 640,
    HEIGHT: 640
};

export const ImageSelectorItem = (props: ImageSelectorItemProps) => {
    const { t } = useTranslation();

    const [imageUrl, setImageUrl] = useState<string>(props.initialImageUrl ?? "");
    const [hasImageSelectionError, setHasImageSelectionError] = useState<boolean>(false);

    const { setValue, name } = props;

    const resizeImageFile = useCallback((file: File): Promise<string> => {
        return new Promise(async (resolve) => {
            const mimeTypeParts = file.type.split("/");

            Resizer.imageFileResizer(
                file,
                MAX_IMAGE_DIMENSION.WIDTH,
                MAX_IMAGE_DIMENSION.HEIGHT,
                mimeTypeParts.length > 1 ? mimeTypeParts[1] : "jpeg",
                100, // image quality
                0, // rotation
                (uri) => {
                    if (typeof uri === "string") {
                        resolve(uri);
                    } else {
                        resolve("");
                    }
                },
                "base64"
            );
        });
    }, []);

    const onHandleChangeInput = useCallback(
        async (event: FileHTMLInputEvent) => {
            if (event?.target?.files) {
                const files = Array.from(event.target.files);

                if (files.length > 0) {
                    const file: File = files[0];
                    const fileUrl = URL.createObjectURL(file);
                    setImageUrl(fileUrl);
                    setValue(`${name}.name`, file.name);
                    setValue(`${name}.url`, fileUrl);
                    setValue(`${name}.id`, uuidv4());
                    setValue(`${name}.type`, props.imagetype);
                    setValue(`${name}.mimetype`, file.type);
                    let base64: string;
                    // resize and load image

                    const isImage = dataTypes.includes(file.type);

                    if (isImage) {
                        setHasImageSelectionError(false);

                        const resizedImageDataURL = await resizeImageFile(file);
                        if (resizedImageDataURL) {
                            base64 = resizedImageDataURL.split(",")[1];
                        } else {
                            base64 = fileUrl.split(",")[1];
                            if (!base64) console.error("error in image processing");
                        }
                        setValue(`${name}.image`, base64);
                    } else {
                        setHasImageSelectionError(true);
                        setValue(name, undefined);
                        setImageUrl("");
                    }
                } else {
                    setValue(name, undefined);
                    setImageUrl("");
                }
            }
        },
        [setValue, resizeImageFile, name, props.imagetype]
    );

    const onDeleteImage = useCallback(() => {
        setImageUrl("");
        setValue(name, undefined);
    }, [setImageUrl, setValue, name]);

    return (
        <>
            <li
                className={`${styles.ImageSelectorItem} ${hasImageSelectionError ? styles.ImageSelectionError : ""}`}
                style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : "none" }}
            >
                {imageUrl === "" ? (
                    <>
                        <span className={styles.ImageSelectorItemLabel}>{props.label}</span>
                        <UI.Icon style={{ marginTop: 14 }} color="var(--grey)" icon={UI.SVGIcon.Camera} />
                        <input
                            className={styles.ImageSelectorFile}
                            type="file"
                            onChange={onHandleChangeInput}
                            accept={dataTypes.concat(extensions).join(",")}
                        />
                    </>
                ) : (
                    <div className={styles.ImageSelectorItemDelete}>
                        <UI.Icon onClick={onDeleteImage} color="var(--grey)" icon={UI.SVGIcon.Trash} />
                    </div>
                )}
            </li>
            {hasImageSelectionError ? (
                <p className={styles.ImageSelectorErrorMessage}>{t("screens.text.image_selection_error")}</p>
            ) : undefined}
        </>
    );
};
